import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Briefcase, Handshake, HardHat, Home, KeyRound, Loader2, Mail } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";
import { TeaLogo } from "@/components/TeaLogo";
import { TeaWordmark } from "@/components/TeaWordmark";
import { RESIDENT_TOKEN_KEY } from "@/pages/PortalLoginPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";
const input = "w-full px-4 py-3 bg-black/40 border border-white/15 rounded-md text-white placeholder:text-white/40 focus:outline-none focus:border-[#d4a017]/70 transition-colors";
const gold = "w-full inline-flex items-center justify-center gap-2 py-3 rounded-md bg-[#d4a017] text-black font-semibold hover:bg-[#e6b52a] disabled:opacity-50 transition-colors";
const pinIn = (set) => (e) => set(e.target.value.replace(/\D/g, "").slice(0, 6));

function useFinish() {
  const { applySession } = useAuth();
  const nav = useNavigate();
  const from = useLocation().state?.from?.pathname;
  return (d) => {
    if (d.kind === "resident") {
      localStorage.setItem(RESIDENT_TOKEN_KEY, d.token);
      if (d.desk_token) { localStorage.setItem("nex_token", d.desk_token); window.location.href = "/admin"; return; }
      nav("/portal/resident"); return;
    }
    applySession(d.token, d.user);
    toast.success(`Welcome back, ${(d.user.name || d.user.email).split(" ")[0]}.`);
    nav(from && from.startsWith("/admin") ? from : "/admin", { replace: true });
  };
}

const GROUPS = [
  { key: "staff", label: "Staff", sub: "TEA team & leadership", Icon: Briefcase, idLabel: "Work email", idType: "email" },
  { key: "partner", label: "Partner / Referring agency", sub: "Caseworkers, agencies, mentors & businesses", Icon: Handshake, idLabel: "Email", idType: "email" },
  { key: "resident", label: "Resident", sub: "Your PACT Life Pass portal", Icon: Home, idLabel: "Mobile number", idType: "tel" },
];

function Choices({ onPick }) {
  return (
    <div className="space-y-3" data-testid="signin-choices">
      {GROUPS.map(({ key, label, sub, Icon }) => (
        <button key={key} type="button" onClick={() => onPick(key)} data-testid={`signin-choice-${key}`}
          className="group w-full flex items-center gap-4 p-4 rounded-lg border border-white/10 bg-white/[0.03] hover:border-[#d4a017]/60 hover:bg-[#d4a017]/[0.06] text-left transition-colors">
          <span className="grid place-items-center h-11 w-11 rounded-md bg-[#d4a017]/15 text-[#d4a017] shrink-0"><Icon className="h-5 w-5" /></span>
          <span className="flex-1 min-w-0"><span className="block font-semibold">{label}</span><span className="block text-xs text-white/50">{sub}</span></span>
          <ArrowRight className="h-4 w-4 text-white/30 group-hover:text-[#d4a017] group-hover:translate-x-0.5 transition-transform" />
        </button>))}
      <Link to="/vendor/login" data-testid="signin-choice-vendor"
        className="group w-full flex items-center gap-4 p-4 rounded-lg border border-white/10 bg-white/[0.03] hover:border-[#d4a017]/60 hover:bg-[#d4a017]/[0.06] text-left transition-colors">
        <span className="grid place-items-center h-11 w-11 rounded-md bg-[#d4a017]/15 text-[#d4a017] shrink-0"><HardHat className="h-5 w-5" /></span>
        <span className="flex-1 min-w-0"><span className="block font-semibold">Contract Vendor</span><span className="block text-xs text-white/50">Employers of TEA residents: hours, invoices & memos</span></span>
        <ArrowRight className="h-4 w-4 text-white/30 group-hover:text-[#d4a017] group-hover:translate-x-0.5 transition-transform" />
      </Link>
    </div>
  );
}

function PinForm({ onCode, group }) {
  const finish = useFinish();
  const [id, setId] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { finish((await axios.post(`${API}/auth/sign-in`, { identifier: id, pin, group: group.key })).data); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="signin-form">
      <input value={id} onChange={(e) => setId(e.target.value)} required autoComplete="username" type={group.idType} inputMode={group.idType === "tel" ? "tel" : "email"} placeholder={group.idLabel} data-testid="signin-identifier" className={input} />
      <input type="password" inputMode="numeric" autoComplete="current-password" value={pin} onChange={pinIn(setPin)} required placeholder="6-digit PIN" data-testid="signin-pin" className={`${input} tracking-[0.5em] font-mono`} />
      <button type="submit" disabled={busy || pin.length !== 6 || !id.trim()} data-testid="signin-submit" className={gold}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Sign in</button>
      <button type="button" onClick={() => onCode(id.includes("@") ? id : "")} data-testid="signin-get-code" className="w-full text-sm text-[#d4a017] hover:underline">First time, or forgot your PIN? Get a code by email</button>
      {group.key === "resident" && <Link to="/login/resident-help" data-testid="signin-resident-help" className="block text-center text-xs text-white/50 hover:text-white">No email on file? Get help signing in</Link>}
    </form>
  );
}

function CodeForm({ initial, onBack }) {
  const finish = useFinish();
  const [email, setEmail] = useState(initial);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); } };
  const send = (e) => { e.preventDefault(); run(async () => { const { data } = await axios.post(`${API}/auth/pin-code/request`, { email }); toast.success(data.message); setSent(true); }); };
  const save = (e) => { e.preventDefault(); run(async () => finish((await axios.post(`${API}/auth/pin-code/verify`, { email, code, pin })).data)); };
  return (
    <form onSubmit={sent ? save : send} className="space-y-4" data-testid="pin-code-form">
      <p className="text-sm text-white/60">{sent ? "Enter the 6-digit code we sent, then choose your new 6-digit PIN." : "Enter the email on your account. We'll send a 6-digit code (and text it if your phone is on file)."}</p>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email" data-testid="pin-code-email" className={input} disabled={sent} />
      {sent && <>
        <input inputMode="numeric" value={code} onChange={pinIn(setCode)} required placeholder="6-digit code" data-testid="pin-code-code" className={`${input} tracking-[0.5em] font-mono`} />
        <input type="password" inputMode="numeric" value={pin} onChange={pinIn(setPin)} required placeholder="New 6-digit PIN" data-testid="pin-code-new-pin" className={`${input} tracking-[0.5em] font-mono`} />
      </>}
      <button type="submit" disabled={busy || (sent && (code.length !== 6 || pin.length !== 6))} data-testid="pin-code-submit" className={gold}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} {sent ? "Set PIN and sign in" : "Email me a code"}</button>
      <div className="flex justify-between text-sm">
        <button type="button" onClick={onBack} data-testid="pin-code-back" className="text-white/50 hover:text-white">← Back to sign in</button>
        {sent && <button type="button" onClick={send} data-testid="pin-code-resend" className="text-[#d4a017] hover:underline">Send a new code</button>}
      </div>
    </form>
  );
}

/** One sign-in for everyone — staff, leadership, partners and residents: email (or resident mobile) + 6-digit PIN, or Google. */
export default function SignInPage() {
  const [code, setCode] = useState(null);
  const [pick, setPick] = useState(null);
  const group = GROUPS.find((g) => g.key === pick);
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/auth/callback";
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div data-testid="signin-page" className="min-h-screen bg-[#07080a] text-white flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-md space-y-8">
        <div className="space-y-2">
          <Link to="/" aria-label="Home"><TeaLogo size={72} testid="signin-logo" className="mb-3" /></Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#d4a017]"><TeaWordmark small text="The Exchange Alliance" testid="signin-wordmark" /> · Sign in</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="signin-title">{code !== null ? "Set your PIN" : group ? group.label : "Welcome back"}</h1>
          {!group && <p className="text-sm text-white/60">How are you signing in today?</p>}
        </div>
        {!group ? <Choices onPick={setPick} /> : (
          <>
            <button type="button" onClick={() => { setPick(null); setCode(null); }} data-testid="signin-change" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Choose a different sign-in</button>
            {code !== null ? <CodeForm initial={code} onBack={() => setCode(null)} /> : <PinForm onCode={setCode} group={group} />}
            {group.key !== "resident" && code === null && <>
              <div className="flex items-center gap-3"><div className="flex-1 h-px bg-white/10" /><span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">or</span><div className="flex-1 h-px bg-white/10" /></div>
              <button type="button" onClick={google} data-testid="google-signin-btn" className="w-full inline-flex items-center justify-center gap-3 py-3 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 transition-colors">
                <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.3C29.5 34.9 26.9 36 24 36c-5.3 0-9.7-3.1-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.5 5.3C41.6 35.6 44 30.2 44 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
                Sign in with Google</button>
              <p className="text-xs text-white/40 text-center">Google works only for emails already on a TEA account.</p>
            </>}
          </>
        )}
      </div>
    </div>
  );
}
